import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import MultiLevelFilter from "@/components/MultiLevelFilter";
import ToolCard from "@/components/ToolCard";
import SortDropdown from "@/components/SortDropdown";
import { tools } from "@/data/tools";
import {
  functionCategories,
  pricingCategories,
  industryCategories
} from "@/data/filterCategories";

export default function Categories() {
  const [selectedFunctionFilters, setSelectedFunctionFilters] = useState<string[]>([]);
  const [selectedPricingFilters, setSelectedPricingFilters] = useState<string[]>([]);
  const [selectedIndustryFilters, setSelectedIndustryFilters] = useState<string[]>([]);
  const [sortOption, setSortOption] = useState<"name" | "popularity" | "date">("popularity");

  const filteredTools = useMemo(() => {
    let result = [...tools];

    // Apply filters
    if (selectedFunctionFilters.length > 0) {
      result = result.filter(tool =>
        selectedFunctionFilters.some(filter =>
          tool.categories.function.includes(filter)
        )
      );
    }

    if (selectedPricingFilters.length > 0) {
      result = result.filter(tool =>
        selectedPricingFilters.includes(tool.price)
      );
    }

    if (selectedIndustryFilters.length > 0) {
      result = result.filter(tool =>
        selectedIndustryFilters.some(filter =>
          tool.categories.industry.includes(filter)
        )
      );
    }

    // Apply sorting
    switch (sortOption) {
      case "name":
        return result.sort((a, b) => a.name.localeCompare(b.name));
      case "popularity":
        return result.sort((a, b) => b.popularity - a.popularity);
      case "date":
        return result.sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      default:
        return result;
    }
  }, [selectedFunctionFilters, selectedPricingFilters, selectedIndustryFilters, sortOption]);

  const handleFunctionFilterChange = (filter: string, isChecked: boolean) => {
    setSelectedFunctionFilters(prev =>
      isChecked ? [...prev, filter] : prev.filter(f => f !== filter)
    );
  };

  const handlePricingFilterChange = (filter: string, isChecked: boolean) => {
    setSelectedPricingFilters(prev =>
      isChecked ? [...prev, filter] : prev.filter(f => f !== filter)
    );
  };

  const handleIndustryFilterChange = (filter: string, isChecked: boolean) => {
    setSelectedIndustryFilters(prev =>
      isChecked ? [...prev, filter] : prev.filter(f => f !== filter)
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Filter sidebar */}
            <div className="w-full md:w-64 flex-shrink-0">
              <div className="bg-white p-4 rounded-lg shadow-md sticky top-24">
                <h2 className="text-xl font-bold mb-4">Filters</h2>
                <MultiLevelFilter
                  title="Function"
                  categories={functionCategories}
                  selectedFilters={selectedFunctionFilters}
                  onFilterChange={handleFunctionFilterChange}
                />
                <MultiLevelFilter
                  title="Pricing"
                  categories={pricingCategories}
                  selectedFilters={selectedPricingFilters}
                  onFilterChange={handlePricingFilterChange}
                />
                <MultiLevelFilter
                  title="Industry"
                  categories={industryCategories}
                  selectedFilters={selectedIndustryFilters}
                  onFilterChange={handleIndustryFilterChange}
                />
                <button
                  onClick={() => {
                    setSelectedFunctionFilters([]);
                    setSelectedPricingFilters([]);
                    setSelectedIndustryFilters([]);
                  }}
                  className="mt-4 text-[#1E90FF] hover:underline text-sm"
                >
                  Clear all filters
                </button>
              </div>
            </div>

            {/* Tool list */}
            <div className="flex-1">
              <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold">
                  {filteredTools.length} AI Tools Found
                </h1>
                <SortDropdown onSortChange={setSortOption} />
              </div>

              {filteredTools.length === 0 ? (
                <div className="bg-white rounded-lg shadow-md p-8 text-center">
                  <i className="fa-regular fa-face-frown text-4xl text-gray-400 mb-4"></i>
                  <h3 className="text-xl font-semibold mb-2">No tools match your filters</h3>
                  <p className="text-gray-600 mb-4">
                    Try adjusting your filters or clear them to see all tools
                  </p>
                  <button
                    onClick={() => {
                      setSelectedFunctionFilters([]);
                      setSelectedPricingFilters([]);
                      setSelectedIndustryFilters([]);
                    }}
                    className="bg-[#1E90FF] text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Clear all filters
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredTools.map(tool => (
                    <ToolCard key={tool.id} tool={tool} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}